var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from 'react';
import { useUXPContext } from '@core/context/useUXPContext';
import { isFeatureAvailable, useFeatureAvailable } from '@core/services/capabilities';
import { useFileUpload, normalizePath, toAbsolute } from '@hooks/useFileUpload';
import { isFontawesomeIcon, isPhosphorIcon } from '@icon/utils';
import { formatFileSize, getFileNameFromUrl, getUrlExtension, inferMediaKind } from '../FileInput/fileHelper';
/** Content-store folder uploads land in unless the field overrides it. */
export const DEFAULT_MEDIA_PATH = 'media-library/';
/** Shortest time an upload reports itself as running, so the progress bar shows. */
const MIN_UPLOAD_FEEDBACK_MS = 600;
/**
 * Holds until the caller's operation has been running for MIN_UPLOAD_FEEDBACK_MS.
 * A batch passes the time it started, so the hold is paid once for the whole
 * batch rather than once per file.
 */
function holdForFeedback(startedAt) {
    return __awaiter(this, void 0, void 0, function* () {
        const remaining = MIN_UPLOAD_FEEDBACK_MS - (Date.now() - startedAt);
        if (remaining > 0)
            yield new Promise(resolve => setTimeout(resolve, remaining));
    });
}
const SYSTEM_APP = 'System';
/** Whether a value is a URL rather than an icon string or free text. */
function isUrlLike(value) {
    const v = (value || '').trim().toLowerCase();
    return v.startsWith('http:') || v.startsWith('https:')
        || v.startsWith('data:') || v.startsWith('blob:') || v.startsWith('/');
}
/**
 * @export
 * Whether a picker value is a font-icon string (`fas:bell`, `phb house`)
 * rather than a URL.
 */
export function isIconValue(value) {
    return isFontawesomeIcon(value) || isPhosphorIcon(value);
}
/**
 * @export
 * Classify a picker value for display. Icon strings are `Icon`; URLs are typed
 * from their extension.
 *
 * Extension-based rather than MIME-based on purpose: a bare value carries no
 * MIME type, and `isImageUrl` (icons/utils) treats *every* http URL as an image.
 * A URL with no usable extension — gallery and hand-typed CDN links routinely
 * have none — is assumed to be an Image, since that is overwhelmingly what they
 * are; renderers fall back to a file icon if it turns out not to load.
 */
export function mediaValueType(value) {
    if (!value)
        return 'Document';
    if (isIconValue(value))
        return 'Icon';
    if (value.trim().toLowerCase().startsWith('data:image/'))
        return 'Image';
    const kind = inferMediaKind(value);
    if (kind === 'image')
        return 'Image';
    if (kind === 'video')
        return 'Video';
    if (kind === 'audio')
        return 'Audio';
    if (!getUrlExtension(value) && isUrlLike(value))
        return 'Image';
    return 'Document';
}
/**
 * @export
 * A short display name for a picker value — the file name for URLs, the icon
 * string itself for icons.
 */
export function mediaValueName(value) {
    if (!value)
        return '';
    if (isIconValue(value))
        return value;
    return getFileNameFromUrl(value);
}
/**
 * @export
 * Classify an upload from its MIME type. Everything that isn't image/video/audio
 * is a Document — including files the browser gave no MIME type for.
 */
export function deriveMediaType(mime) {
    const m = (mime || '').toLowerCase();
    if (m.startsWith('image/'))
        return 'Image';
    if (m.startsWith('video/'))
        return 'Video';
    if (m.startsWith('audio/'))
        return 'Audio';
    return 'Document';
}
/**
 * @export
 * The upload restriction implied by a set of media types. `Document` accepts
 * anything, so it clears the restriction entirely.
 */
export function resolveAllowedTypes(mediaTypes) {
    const types = mediaTypes || [];
    if (types.indexOf('Document') >= 0)
        return undefined;
    const allowed = [];
    if (types.indexOf('Image') >= 0 || types.indexOf('Icon') >= 0)
        allowed.push('image/*');
    if (types.indexOf('Video') >= 0)
        allowed.push('video/*');
    if (types.indexOf('Audio') >= 0)
        allowed.push('audio/*');
    return allowed.length ? allowed : undefined;
}
/**
 * @export
 * Append values that aren't already present, preserving order. Every source
 * adds to the selection this way.
 */
export function addUnique(list, values) {
    const next = [...list];
    values.forEach(v => { if (next.indexOf(v) < 0)
        next.push(v); });
    return next;
}
/**
 * @export
 * Absolute URL a stored library file is served from. Built exactly like
 * `uploadFile` builds the URL it returns, so a record's Path and the value
 * emitted at upload time always agree.
 */
export function mediaItemUrl(context, path) {
    // A path that is already a full link (e.g. an external file) is served as it is
    if (!(context === null || context === void 0 ? void 0 : context.lucyUrl) || /^https?:\/\//i.test(path || ''))
        return path;
    const base = context.lucyUrl.endsWith('/') ? context.lucyUrl : context.lucyUrl + '/';
    return toAbsolute(`${base}content/${path || ''}`);
}
/**
 * @export
 * One page of the caller's own uploads. Never throws — a missing or failing
 * service surfaces as `error` so the picker can show a graceful empty state.
 */
export function fetchMyMedia(context, options) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        const { data, error } = yield context.executeServiceSafe(SYSTEM_APP, 'MediaItem:MyMedia', Object.assign(Object.assign(Object.assign({ max: (_a = options === null || options === void 0 ? void 0 : options.max) !== null && _a !== void 0 ? _a : 24, last: (_b = options === null || options === void 0 ? void 0 : options.last) !== null && _b !== void 0 ? _b : 0, q: (options === null || options === void 0 ? void 0 : options.q) || '', types: ((options === null || options === void 0 ? void 0 : options.types) || []).join(',') }, ((options === null || options === void 0 ? void 0 : options.folder) ? { folder: options.folder } : {})), ((options === null || options === void 0 ? void 0 : options.tag) ? { tag: options.tag } : {})), ((options === null || options === void 0 ? void 0 : options.shared) ? { shared: 1 } : {})), { json: true, executeImmidiately: true });
        if (error)
            return { items: [], total: 0, error };
        const items = (data || []);
        return { items, total: Number((_d = (_c = items[0]) === null || _c === void 0 ? void 0 : _c.TotalCount) !== null && _d !== void 0 ? _d : items.length) };
    });
}
/**
 * @export
 * The user's folders and the folders shared with them. Never throws: an older server
 * (no folders) answers with `error`.
 */
export function fetchMediaFolders(context) {
    return __awaiter(this, void 0, void 0, function* () {
        const { data, error } = yield context.executeServiceSafe(SYSTEM_APP, 'MediaItem:Folders', {}, { json: true, executeImmidiately: true });
        return { items: error ? [] : (data || []), error };
    });
}
/**
 * @export
 * The user's tags and the tags shared with them. Never throws.
 */
export function fetchMediaTags(context) {
    return __awaiter(this, void 0, void 0, function* () {
        const { data, error } = yield context.executeServiceSafe(SYSTEM_APP, 'MediaItem:Tags', {}, { json: true, executeImmidiately: true });
        return { items: error ? [] : (data || []), error };
    });
}
/**
 * @export
 * Record an uploaded file in the library. Ownership is stamped server-side
 * from the session, so no user key is sent.
 */
export function createMediaItem(context, item) {
    return __awaiter(this, void 0, void 0, function* () {
        return context.executeServiceSafe(SYSTEM_APP, 'MediaItem:Create', item, { json: true, executeImmidiately: true });
    });
}
// uxp ships from a CDN and updates for every deployment at once; the server
// deploys per account and lags. So the media library may simply not exist on the
// server this bundle happens to be talking to, and the picker has to notice
// rather than show a broken source. `capabilities.ts` owns how that is decided
// and cached; this only says which call answers the question for the library.
//
// Listing one row is the cheapest honest probe — it is exactly the call the
// library source needs, and `MediaItem:MyMedia` is model-scoped, so an older
// server fails it on the missing model rather than the missing service.
const LIBRARY_PROBE = {
    type: 'service',
    app: SYSTEM_APP,
    service: 'MediaItem:MyMedia',
    parameters: { max: 1, last: 0, q: '', types: '' },
    options: { json: true, executeImmidiately: true },
};
/**
 * @export
 * Whether this deployment's server has the media library at all.
 */
export function getMediaLibraryAvailable(context) {
    return isFeatureAvailable(context, LIBRARY_PROBE);
}
/**
 * @export
 * `null` until the probe resolves. Probes only while `active`, so a picker whose
 * library source is switched off never asks.
 */
export function useMediaLibraryAvailable(active) {
    return useFeatureAvailable(LIBRARY_PROBE, { active });
}
// Account-wide "track uploads in the media library" flag, fetched once per page
// load. An admin flipping it in System settings therefore won't reach an
// already-open session until it reloads — acceptable for a setting that changes
// about never, and a field can always override it with `saveToLibrary`.
let saveToLibrarySetting = null;
/**
 * @export
 * The account setting for recording uploads. Default is ON: only an explicit
 * `'0'` turns it off, so an unset key (or an unreachable service) still tracks.
 */
export function getSaveToLibrarySetting(context) {
    if (!saveToLibrarySetting) {
        saveToLibrarySetting = context.executeServiceSafe(SYSTEM_APP, 'SpecificAccountSettings', {
            'MediaLibrary.SaveUploads': 'MediaLibrary.SaveUploads',
        }, { json: true, executeImmidiately: true })
            .then(({ data, error }) => {
            if (error)
                return true;
            const row = Array.isArray(data) ? data[0] : data;
            return (row === null || row === void 0 ? void 0 : row['MediaLibrary.SaveUploads']) !== '0';
        })
            .catch(() => true);
    }
    return saveToLibrarySetting;
}
/**
 * @export
 * Read an image's size in pixels. Resolves null when it does not load as an image.
 */
export function readImagePixels(url) {
    return new Promise(resolve => {
        if (!url)
            return resolve(null);
        const img = new Image();
        img.onload = () => resolve(img.naturalWidth ? { width: img.naturalWidth, height: img.naturalHeight } : null);
        img.onerror = () => resolve(null);
        img.src = url;
    });
}
/**
 * @export
 * Read the print resolution (DPI) a JPEG or PNG records: JPEG's JFIF density, else its EXIF
 * XResolution; PNG's pHYs. Resolves undefined when the file records none.
 */
export function readImageDpi(blob) {
    return __awaiter(this, void 0, void 0, function* () {
        // The headers that carry it are at the start of the file
        const b = new DataView(yield blob.slice(0, 256 * 1024).arrayBuffer());
        const perCm = (v) => v * 2.54;
        if (b.byteLength > 24 && b.getUint32(0) === 0x89504E47) {
            // PNG chunks: length, type, data, crc
            for (let p = 8; p + 8 <= b.byteLength;) {
                const len = b.getUint32(p), type = b.getUint32(p + 4);
                if (type === 0x70485973 && p + 17 <= b.byteLength) { // pHYs: per metre when unit is 1
                    return b.getUint8(p + 16) === 1 ? Math.round(b.getUint32(p + 8) * 0.0254) : undefined;
                }
                if (type === 0x49444154)
                    return undefined; // image data started: no pHYs
                p += 12 + len;
            }
            return undefined;
        }
        if (b.byteLength < 4 || b.getUint16(0) !== 0xFFD8)
            return undefined;
        let exifDpi;
        for (let p = 2; p + 4 <= b.byteLength;) {
            const marker = b.getUint16(p), len = b.getUint16(p + 2), d = p + 4;
            if (marker === 0xFFDA)
                break; // image data
            if (marker === 0xFFE0 && d + 12 <= b.byteLength && b.getUint32(d) === 0x4A464946) { // JFIF
                const units = b.getUint8(d + 7), x = b.getUint16(d + 8);
                if (units === 1 && x)
                    return x;
                if (units === 2 && x)
                    return Math.round(perCm(x));
            }
            if (marker === 0xFFE1 && d + 14 <= b.byteLength && b.getUint32(d) === 0x45786966) { // Exif
                exifDpi = readExifDpi(b, d + 6);
            }
            p += 2 + len;
        }
        return exifDpi;
    });
}
// EXIF IFD0: XResolution (0x011A, a rational) and ResolutionUnit (0x0128: 2 inch, 3 cm)
function readExifDpi(b, tiff) {
    try {
        const le = b.getUint16(tiff) === 0x4949;
        const u16 = (o) => b.getUint16(tiff + o, le), u32 = (o) => b.getUint32(tiff + o, le);
        const ifd = u32(4), count = u16(ifd);
        let x, unit = 2;
        for (let i = 0; i < count; i++) {
            const e = ifd + 2 + i * 12, tag = u16(e);
            if (tag === 0x011A) {
                const off = u32(e + 8), den = u32(off + 4);
                x = den ? u32(off) / den : undefined;
            }
            if (tag === 0x0128)
                unit = u16(e + 8);
        }
        if (!x)
            return undefined;
        return Math.round(unit === 3 ? x * 2.54 : x);
    }
    catch (_a) {
        return undefined;
    }
}
// CRC-32 (PNG chunks), table built on first use
let crcTable = null;
function crc32(bytes) {
    if (!crcTable) {
        crcTable = new Uint32Array(256);
        for (let n = 0; n < 256; n++) {
            let c = n;
            for (let k = 0; k < 8; k++)
                c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
            crcTable[n] = c >>> 0;
        }
    }
    let crc = 0xFFFFFFFF;
    for (let i = 0; i < bytes.length; i++)
        crc = crcTable[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8);
    return (crc ^ 0xFFFFFFFF) >>> 0;
}
/**
 * @export
 * Write a print resolution (DPI) into a JPEG or PNG: JPEG's JFIF density (the APP0 segment is
 * patched, or added when missing), PNG's pHYs chunk (replaced or added). Other files come back
 * unchanged.
 */
export function setImageDpi(blob, dpi) {
    return __awaiter(this, void 0, void 0, function* () {
        const value = Math.round(Number(dpi));
        if (!blob || !isFinite(value) || value <= 0)
            return blob;
        const bytes = new Uint8Array(yield blob.arrayBuffer());
        const view = new DataView(bytes.buffer);
        // PNG: drop any pHYs, add one after IHDR (pixels per metre, unit 1)
        if (bytes.length > 33 && view.getUint32(0) === 0x89504E47) {
            const ppm = Math.round(value / 0.0254);
            const chunk = new Uint8Array(21);
            const c = new DataView(chunk.buffer);
            c.setUint32(0, 9);
            chunk.set([0x70, 0x48, 0x59, 0x73], 4); // 'pHYs'
            c.setUint32(8, ppm);
            c.setUint32(12, ppm);
            chunk[16] = 1;
            c.setUint32(17, crc32(chunk.subarray(4, 17)));
            const parts = [bytes.subarray(0, 8)];
            let p = 8;
            while (p + 12 <= bytes.length) {
                const len = view.getUint32(p), type = view.getUint32(p + 4);
                const end = Math.min(bytes.length, p + 12 + len);
                if (type !== 0x70485973)
                    parts.push(bytes.subarray(p, end));
                if (type === 0x49484452)
                    parts.push(chunk); // right after IHDR
                p = end;
            }
            if (p < bytes.length)
                parts.push(bytes.subarray(p));
            return new Blob(parts, { type: blob.type || 'image/png' });
        }
        // JPEG: patch the JFIF APP0 density (units 1 = dots per inch), or add an APP0
        if (bytes.length > 4 && view.getUint16(0) === 0xFFD8) {
            const density = Math.min(value, 0xFFFF);
            for (let p = 2; p + 4 <= bytes.length;) {
                const marker = view.getUint16(p), len = view.getUint16(p + 2), d = p + 4;
                if ((marker & 0xFF00) !== 0xFF00 || marker === 0xFFDA)
                    break;
                if (marker === 0xFFE0 && d + 12 <= bytes.length && view.getUint32(d) === 0x4A464946) {
                    const copy = bytes.slice();
                    const out = new DataView(copy.buffer);
                    out.setUint8(d + 7, 1);
                    out.setUint16(d + 8, density);
                    out.setUint16(d + 10, density);
                    return new Blob([copy], { type: blob.type || 'image/jpeg' });
                }
                p += 2 + len;
            }
            const app0 = new Uint8Array([
                0xFF, 0xE0, 0x00, 0x10, 0x4A, 0x46, 0x49, 0x46, 0x00, 0x01, 0x01, 0x01,
                density >> 8, density & 0xFF, density >> 8, density & 0xFF, 0x00, 0x00,
            ]);
            return new Blob([bytes.subarray(0, 2), app0, bytes.subarray(2)], { type: blob.type || 'image/jpeg' });
        }
        return blob;
    });
}
/**
 * @export
 * Whether a file type can be cropped in the browser: raster images, not SVG or animated GIF.
 */
export function isCroppableImage(mimeType) {
    const m = (mimeType || '').toLowerCase();
    return m.startsWith('image/') && m !== 'image/svg+xml' && m !== 'image/gif';
}
/**
 * @export
 * Run a field's check on a value. Images are loaded first for their pixels and DPI.
 * Resolves the message, or undefined when the value is fine (or no check is set).
 */
export function runMediaCheck(validate, info) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!validate)
            return undefined;
        const isImage = info.mimeType ? info.mimeType.toLowerCase().startsWith('image/') : mediaValueType(info.value) === 'Image';
        let full = info;
        if (isImage && info.width === undefined) {
            const pixels = yield readImagePixels(info.value);
            if (pixels)
                full = Object.assign(Object.assign({}, info), pixels);
        }
        if (isImage && info.dpi === undefined) {
            // ponytail: fetches the picture again (usually from the browser cache); a site that blocks it just gets no DPI check
            try {
                const res = yield fetch(info.value);
                if (res.ok)
                    full = Object.assign(Object.assign({}, full), { dpi: yield readImageDpi(yield res.blob()) });
            }
            catch ( /* unknown DPI is not checked, as on the server */_a) { /* unknown DPI is not checked, as on the server */ }
        }
        try {
            return validate(full) || undefined;
        }
        catch (e) {
            return (e === null || e === void 0 ? void 0 : e.message) || String(e);
        }
    });
}
/**
 * @export
 * Run a field's check on a file not yet uploaded.
 */
export function runFileCheck(validate, file) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!validate || !file)
            return undefined;
        const url = URL.createObjectURL(file);
        try {
            return yield runMediaCheck(validate, { value: url, name: file.name, mimeType: file.type, size: file.size });
        }
        finally {
            URL.revokeObjectURL(url);
        }
    });
}
/** The reason an upload error carries, or '' when it carries none. */
function uploadErrorMessage(e) {
    if (!e)
        return '';
    if (typeof e === 'string')
        return e;
    return typeof e.message === 'string' ? e.message : '';
}
// A file size for people (lives with the FileInput helpers, which show it too)
export { formatFileSize };
/**
 * @export
 * The stored types a library lists for a picker's media types. `Icon` is
 * listed as `Image`: font icons are not stored, and icon files are images.
 */
export function libraryTypes(mediaTypes) {
    const types = [];
    for (const t of mediaTypes || []) {
        const stored = t === 'Icon' ? 'Image' : t;
        if (types.indexOf(stored) < 0)
            types.push(stored);
    }
    return types;
}
/**
 * @export
 * Upload straight into the content store and — when enabled — record the file
 * in the media library. The upload URL is returned either way: a failed
 * library write must never cost the user their file.
 *
 * `progress` is the transfer percentage (0-100) of the content-store PUT; the
 * library write that follows it is a single small call and isn't counted.
 * `uploadingIndex` / `uploadingTotal` describe the batch a multi-file upload is
 * working through, so callers can render "Uploading 2 of 5".
 *
 * `isUploading` stays true for a beat after the transfer finishes — see
 * MIN_UPLOAD_FEEDBACK_MS.
 */
export function useLibraryUpload(options) {
    const context = useUXPContext();
    const { upload, isUploading, progress, error } = useFileUpload();
    // A local upload finishes in a few milliseconds, which would flash the file
    // preview and the progress bar past far too quickly to register. Holding the
    // uploading state briefly makes that feedback visible; a slow upload already
    // exceeds this and is unaffected.
    const [holding, setHolding] = React.useState(false);
    const [uploadingIndex, setUploadingIndex] = React.useState(0);
    const [uploadingTotal, setUploadingTotal] = React.useState(0);
    // Single-select closes the dialog the moment the upload resolves, so the
    // clean-up below routinely runs against an unmounted view.
    const mounted = React.useRef(true);
    React.useEffect(() => () => { mounted.current = false; }, []);
    const clearProgress = React.useCallback(() => {
        if (!mounted.current)
            return;
        setHolding(false);
        setUploadingIndex(0);
        setUploadingTotal(0);
    }, []);
    const uploadPath = (options === null || options === void 0 ? void 0 : options.uploadPath) || DEFAULT_MEDIA_PATH;
    const saveToLibrary = options === null || options === void 0 ? void 0 : options.saveToLibrary;
    // The transfer itself, with none of the batch bookkeeping or the feedback
    // hold — those belong to the whole operation, not to each file in it.
    const uploader = options === null || options === void 0 ? void 0 : options.uploader;
    const folderKey = options === null || options === void 0 ? void 0 : options.folderKey;
    const uploadOne = React.useCallback((file) => __awaiter(this, void 0, void 0, function* () {
        if (uploader)
            return uploader(file);
        const path = normalizePath(uploadPath);
        const { url, fileName } = yield upload(file, { path });
        // Capability first, and it outranks `saveToLibrary`: a field asking to
        // record uploads can't conjure a model the server doesn't have, and
        // checking it first means an older server skips the settings fetch too.
        // Both are cached promises, so neither costs a request after the first.
        const save = (yield getMediaLibraryAvailable(context))
            && (saveToLibrary !== undefined ? saveToLibrary : yield getSaveToLibrarySetting(context));
        if (save) {
            const { error: createError } = yield createMediaItem(context, Object.assign({ Name: file.name, Path: path + fileName, MediaType: deriveMediaType(file.type), MimeType: file.type, Size: file.size }, (folderKey ? { FolderKey: folderKey } : {})));
            if (createError)
                console.warn('Uploaded file was not recorded in the media library.', createError);
        }
        return url;
    }), [context, upload, uploadPath, saveToLibrary, uploader, folderKey]);
    const uploadToLibrary = React.useCallback((file) => __awaiter(this, void 0, void 0, function* () {
        const startedAt = Date.now();
        setHolding(true);
        setUploadingIndex(1);
        setUploadingTotal(1);
        try {
            const url = yield uploadOne(file);
            yield holdForFeedback(startedAt);
            return url;
        }
        finally {
            clearProgress();
        }
    }), [uploadOne, clearProgress]);
    /**
     * Upload several files one after another. A file that fails is recorded and
     * the rest still go up — a bad file in the middle must not cost the user the
     * ones that worked. `failed` keeps the file names (as it always has);
     * `errors` carries each failed file with the reason it failed.
     * `options` reports each file as it starts and finishes, so a caller can
     * draw a row per file.
     */
    const uploadAllToLibrary = React.useCallback((files, uploadOptions) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        const list = files || [];
        const urls = [];
        const failed = [];
        const errors = [];
        const startedAt = Date.now();
        setHolding(true);
        setUploadingTotal(list.length);
        try {
            for (let i = 0; i < list.length; i++) {
                setUploadingIndex(i + 1);
                (_a = uploadOptions === null || uploadOptions === void 0 ? void 0 : uploadOptions.onFileStart) === null || _a === void 0 ? void 0 : _a.call(uploadOptions, list[i], i);
                try {
                    const url = yield uploadOne(list[i]);
                    urls.push(url);
                    (_b = uploadOptions === null || uploadOptions === void 0 ? void 0 : uploadOptions.onFileDone) === null || _b === void 0 ? void 0 : _b.call(uploadOptions, list[i], i, { url });
                }
                catch (e) {
                    const message = uploadErrorMessage(e);
                    failed.push(list[i].name);
                    errors.push({ file: list[i], message });
                    (_c = uploadOptions === null || uploadOptions === void 0 ? void 0 : uploadOptions.onFileDone) === null || _c === void 0 ? void 0 : _c.call(uploadOptions, list[i], i, { error: message });
                }
            }
            yield holdForFeedback(startedAt);
            return { urls, failed, errors };
        }
        finally {
            clearProgress();
        }
    }), [uploadOne, clearProgress]);
    return { uploadToLibrary, uploadAllToLibrary, isUploading: isUploading || holding, progress, uploadingIndex, uploadingTotal, error };
}
